import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import { Helmet } from 'react-helmet-async';

const ToolsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <PublicNavBar />
      <Helmet>
        <title>AI Tools for Teachers | TeAIch Assess and TeAIch Planner</title>
        <meta name="description" content="TeAIch Assess marks BTEC, GCSE and A-Level work in seconds. TeAIch Planner generates differentiated lesson plans instantly. Built for teachers worldwide." />
        <link rel="canonical" href="https://teaich.app/tools" />
      </Helmet>

      <div className="py-8 sm:py-12 md:py-16 px-2 sm:px-4">
        <div className="max-w-5xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12 sm:mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-coral-500 mb-6 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              AI TOOLS FOR TEACHERS
            </h1>
            <p className="text-lg md:text-xl text-ink-700 max-w-3xl mx-auto">
              TeAIch Suite gives teachers worldwide two powerful AI tools to cut marking time and create better lessons.
              Trusted by educators teaching BTEC, GCSE, A-Level, Primary, Secondary, and beyond.
            </p>
          </div>

          {/* TeAIch Assess */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/images/TeaichAssessAppLogo.png"
                alt="TeAIch Assess - AI Marking Tool for Teachers"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-coral-500">TeAIch Assess</h2>
                <p className="text-ink-600 font-medium">AI Marking Tool for Teachers</p>
              </div>
            </div>

            <p className="text-ink-700 text-lg mb-6 leading-relaxed">
              TeAIch Assess is an AI-powered marking tool designed for teachers worldwide.
              Upload your mark scheme and student work, and receive detailed criterion-by-criterion
              feedback in seconds. Built for BTEC Pass, Merit, and Distinction grading, GCSE,
              A-Level, and custom marking schemes.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  title: 'Upload Your Mark Scheme',
                  desc: 'Upload BTEC, GCSE, A-Level, or custom marking criteria. Supports PDF, Word, and text formats.',
                  step: '1'
                },
                {
                  title: 'Upload Student Work',
                  desc: 'Upload student assignments as PDFs or images. Our AI uses advanced OCR to read handwritten and typed work.',
                  step: '2'
                },
                {
                  title: 'Get Instant Feedback',
                  desc: 'Receive criterion-by-criterion assessment, an overall grade, detailed feedback, strengths, and areas for improvement.',
                  step: '3'
                },
              ].map(({ title, desc, step }) => (
                <div key={step} className="text-center">
                  <div className="w-12 h-12 bg-coral-300 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold text-lg">{step}</span>
                  </div>
                  <h3 className="font-bold text-ink-900 mb-2">{title}</h3>
                  <p className="text-ink-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { icon: 'fast', label: 'LIGHTNING FAST', desc: 'Mark 30 pieces of work in under 5 minutes' },
                { icon: 'accurate', label: 'BTEC READY', desc: 'Built for Pass, Merit, Distinction grading hierarchies' },
                { icon: 'detailed', label: 'DETAILED FEEDBACK', desc: 'Criterion-by-criterion breakdown for every student' },
                { icon: 'pdf', label: 'PDF REPORTS', desc: 'Download professional feedback reports instantly' },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-sand-50 border-2 border-sand-200 rounded-lg p-4 text-center">
                  <h4 className="font-bold text-ink-900 text-sm mb-1">{label}</h4>
                  <p className="text-ink-600 text-xs">{desc}</p>
                </div>
              ))}
            </div>

          </div>

          {/* TeAIch Planner */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/images/TeaichPlannerAppLogo.png"
                alt="TeAIch Planner - AI Lesson Planning Tool for Teachers"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-coral-500">TeAIch Planner</h2>
                <p className="text-ink-600 font-medium">AI Lesson Planning Tool for Teachers</p>
              </div>
            </div>

            <p className="text-ink-700 text-lg mb-6 leading-relaxed">
              TeAIch Planner is an AI lesson plan generator built for teachers everywhere. Tell our AI
              your subject, year group, and topic, and receive a complete, differentiated lesson
              plan in seconds. Also includes a resource generator for worksheets, activities,
              and teaching materials.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  title: 'Tell Us Your Lesson',
                  desc: 'Enter your subject, year group, topic, and any special requirements such as SEN support or EAL considerations.',
                  step: '1'
                },
                {
                  title: 'Get Your Lesson Plan',
                  desc: 'Receive a complete lesson plan with objectives, starter activity, main activities, plenary, and differentiation built in.',
                  step: '2'
                },
                {
                  title: 'Save and Create Resources',
                  desc: 'Save plans to your library, download as PDF, and create matching worksheets and resources with one click.',
                  step: '3'
                },
              ].map(({ title, desc, step }) => (
                <div key={step} className="text-center">
                  <div className="w-12 h-12 bg-coral-300 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-white font-bold text-lg">{step}</span>
                  </div>
                  <h3 className="font-bold text-ink-900 mb-2">{title}</h3>
                  <p className="text-ink-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'ALL SUBJECTS', desc: 'Maths, English, Science, BTEC, and more' },
                { label: 'DIFFERENTIATED', desc: 'Built-in SEN, EAL, and ability differentiation' },
                { label: 'RESOURCE GENERATOR', desc: 'Create worksheets and activities instantly' },
                { label: 'LESSON LIBRARY', desc: 'Save and reuse your lesson plans any time' },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-sand-50 border-2 border-sand-200 rounded-lg p-4 text-center">
                  <h4 className="font-bold text-ink-900 text-sm mb-1">{label}</h4>
                  <p className="text-ink-600 text-xs">{desc}</p>
                </div>
              ))}
            </div>

          </div>

          {/* TeAIch Insights */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 mb-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-coral-300 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-2xl">&#128202;</span>
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-coral-500">TeAIch Insights</h2>
                <p className="text-ink-600 font-medium">Class Performance Dashboard</p>
              </div>
            </div>

            <p className="text-ink-700 text-lg mb-6 leading-relaxed">
              TeAIch Insights gives teachers and heads of department a powerful overview of class
              performance across all marked briefs. See exactly which criteria students are
              struggling with, track grade distributions, and identify teaching focus areas
              all in one place.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {[
                { label: 'Criterion Achievement Rates', desc: 'See what percentage of your class achieved each Pass, Merit, and Distinction criterion. Instantly identify where to focus your teaching.' },
                { label: 'Grade Distribution Charts', desc: 'Visual breakdown of Distinction, Merit, Pass, and Fail rates across the whole class for any brief.' },
                { label: 'Most Missed Criteria', desc: 'Instantly see which criterion is most commonly missed so you can target your feedback and reteaching.' },
                { label: 'Brief-by-Brief Drill Down', desc: 'Click into any assignment brief to see detailed criterion-level data for that specific piece of work.' },
              ].map(({ label, desc }) => (
                <div key={label} className="bg-sand-50 border-2 border-sand-200 rounded-lg p-4">
                  <h3 className="font-bold text-ink-900 mb-2">{label}</h3>
                  <p className="text-ink-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

          </div>

          {/* Who is it for */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-ink-900 text-center mb-8">
              WHO IS TEAICH FOR?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: 'College Lecturers',
                  desc: 'TeAIch Assess is purpose-built for BTEC grading with Pass, Merit, and Distinction criteria hierarchies. Perfect for FE and sixth form colleges.'
                },
                {
                  title: 'All Teachers',
                  desc: 'Whether you teach Primary, Secondary, or Further Education, TeAIch works for every subject and level. Plan lessons and mark work faster with AI assistance.'
                },
                {
                  title: 'Heads of Department',
                  desc: 'The Insights Dashboard shows class-level performance data across all marked briefs, helping you identify where students need extra support.'
                },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-sand-50 rounded-lg p-6 border-2 border-sand-200">
                  <h3 className="font-bold text-ink-900 text-lg mb-3">{title}</h3>
                  <p className="text-ink-600 text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ - SEO value */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-ink-900 text-center mb-8">
              FREQUENTLY ASKED QUESTIONS
            </h2>
            <div className="space-y-6 max-w-3xl mx-auto">
              {[
                {
                  q: 'Can TeAIch Assess mark BTEC work?',
                  a: 'Yes — TeAIch Assess is specifically designed for BTEC-style grading. It assesses Pass, Merit, and Distinction criteria individually and enforces the correct hierarchy: Merit requires all Pass criteria to be met, and Distinction requires all Pass and Merit criteria.'
                },
                {
                  q: 'What file formats does TeAIch support?',
                  a: 'TeAIch Assess supports PDF files, images (JPG, PNG, GIF, BMP, WebP), and plain text files. PDFs give the most accurate results as our AI uses advanced OCR technology to extract text.'
                },
                {
                  q: 'How long does AI marking take?',
                  a: 'Most marking results are returned in under 10 seconds. The AI reads the student work, assesses it against your mark scheme criterion by criterion, and returns a grade with detailed feedback.'
                },
                {
                  q: 'Can I use TeAIch Planner for any subject?',
                  a: 'Yes — TeAIch Planner works for all secondary and FE subjects including Maths, English, Science, History, Business, Computing, and vocational subjects.'
                },
                {
                  q: 'Is AI marking accurate enough to use with students?',
                  a: 'TeAIch uses advanced AI models optimised for educational assessment. We always recommend applying your professional judgement before sharing AI feedback with students. The AI is a tool to assist you, not replace you.'
                },
                {
                  q: 'Does TeAIch work for GCSE and A-Level marking?',
                  a: 'Yes — you can upload any mark scheme including GCSE, A-Level, and custom rubrics. The AI will assess student work against whatever criteria you provide.'
                },
              ].map(({ q, a }) => (
                <div key={q}>
                  <h3 className="font-bold text-ink-900 mb-2">{q}</h3>
                  <p className="text-ink-600 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-coral-300 rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              READY TO SAVE HOURS EVERY WEEK?
            </h2>
            <p className="text-white text-lg mb-8 opacity-90">
              Join teachers across the world using TeAIch to mark faster and plan better.
            </p>
            <button
              onClick={() => navigate('/pricing')}
              className="bg-white text-coral-600 px-10 py-4 rounded-lg font-bold text-lg hover:bg-sand-50 transition duration-200"
            >
              See Pricing
            </button>
          </div>

        </div>
      </div>

      <footer className="bg-sand-100 border-t-4 border-coral-300 py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/images/craidle-logo.png" alt="Craidle Intelligence Group" className="h-10 sm:h-14 w-auto" />
            </div>
            <div className="flex space-x-4">
              <a href="YOUR_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/teaich_app/" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="YOUR_TIKTOK_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.27 8.27 0 004.84 1.56V6.81a4.85 4.85 0 01-1.07-.12z"/>
                </svg>
              </a>
              <a href="YOUR_LINKEDIN_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <button onClick={() => navigate('/feedback')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Feedback</button>
              <button onClick={() => navigate('/legal/privacy-policy')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Privacy Policy</button>
              <button onClick={() => navigate('/legal/terms-of-service')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Terms of Service</button>
              <button onClick={() => navigate('/legal/data-retention')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Data Retention</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ToolsPage;